import React from 'react';
import {
  Music, Trophy, Tent, Building2, Beer, PartyPopper, Lock, Tag,
  MapPin, Clock, DollarSign, ExternalLink, Users, CalendarClock,
  type LucideIcon,
} from 'lucide-react';
import { Icon } from './Icon';
import type { CantoEvent, Reservation, SportsDetails, TicketmasterDetails } from '../api/types';
import '../styles/event-cards.css';

// ---------------------------------------------------------------------------
// Helpers (exported for use in UpcomingView)
// ---------------------------------------------------------------------------

export function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

export function sod(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function eod(d: Date): Date {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

export function eventOnDay(ev: CantoEvent, dayStart: Date, dayEnd: Date): boolean {
  return new Date(ev.startAt) <= dayEnd && new Date(ev.endAt) >= dayStart;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TYPE_ICON: Record<string, 'alert' | 'book' | 'pin' | 'menu'> = {
  sporting_event: 'alert',
  concert:        'book',
  festival:       'pin',
  beer_release:   'pin',
  party:          'pin',
  convention:     'pin',
  private_event:  'pin',
  other:          'menu',
};

const EVENT_TYPE_LABEL: Record<string, string> = {
  sporting_event: 'Sporting Event',
  concert:        'Concert',
  festival:       'Festival',
  beer_release:   'Beer Release',
  party:          'Party',
  convention:     'Convention',
  private_event:  'Private Event',
  other:          'Event',
};

interface TmBannerMeta {
  gradient: string;
  labelColor: string;
  Icon: LucideIcon;
}

const TM_BANNER_META: Record<string, TmBannerMeta> = {
  concert:        { gradient: 'linear-gradient(135deg,#1e1b4b,#4c1d95,#6d28d9)', labelColor: '#c4b5fd', Icon: Music },
  sporting_event: { gradient: 'linear-gradient(135deg,#0c1a3a,#1e3a6e,#1d4ed8)', labelColor: '#93c5fd', Icon: Trophy },
  festival:       { gradient: 'linear-gradient(135deg,#052e16,#14532d,#15803d)', labelColor: '#86efac', Icon: Tent },
  convention:     { gradient: 'linear-gradient(135deg,#451a03,#7c2d12,#b45309)', labelColor: '#fcd34d', Icon: Building2 },
  beer_release:   { gradient: 'linear-gradient(135deg,#422006,#78350f,#d97706)', labelColor: '#fde68a', Icon: Beer },
  party:          { gradient: 'linear-gradient(135deg,#4c0519,#881337,#be185d)', labelColor: '#fda4af', Icon: PartyPopper },
  private_event:  { gradient: 'linear-gradient(135deg,#0f172a,#1e293b,#475569)', labelColor: '#cbd5e1', Icon: Lock },
  other:          { gradient: 'linear-gradient(135deg,#111,#222,#404040)',        labelColor: '#d1d5db', Icon: Tag },
};

const TM_BANNER_DEFAULT: TmBannerMeta = TM_BANNER_META['other']!;

const IMPACT_META = {
  high:   { label: 'HIGH',   dot: '●●●' },
  medium: { label: 'MEDIUM', dot: '●●○' },
  low:    { label: 'LOW',    dot: '●○○' },
} as const;

// ---------------------------------------------------------------------------
// ImpactBadge (internal)
// ---------------------------------------------------------------------------

function ImpactBadge({ level }: { level: 'high' | 'medium' | 'low' }): React.ReactElement {
  const meta = IMPACT_META[level];
  return (
    <span className={`home-impact-badge${level === 'high' ? ' home-impact-badge--high' : ''}`}>
      <span className="home-impact-dot">{meta.dot}</span>
      {meta.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// TicketmasterEventChip (internal)
// ---------------------------------------------------------------------------

function TicketmasterEventChip({
  event,
  details,
}: {
  event: CantoEvent;
  details: TicketmasterDetails;
}): React.ReactElement {
  const banner = TM_BANNER_META[event.type] ?? TM_BANNER_DEFAULT;
  const BannerIcon = banner.Icon;
  const genreLabel = details.genre ?? EVENT_TYPE_LABEL[event.type] ?? event.type;

  const multiDay = event.startAt.slice(0, 10) !== event.endAt.slice(0, 10);
  const timeRange = multiDay ? 'All day' : `${formatTime(event.startAt)} – ${formatTime(event.endAt)}`;
  const startTime = multiDay ? 'All day' : formatTime(event.startAt);

  const impact: 'high' | 'medium' | 'low' | null =
    event.expectedImpact === 'low' || event.expectedImpact === 'medium' || event.expectedImpact === 'high'
      ? event.expectedImpact
      : null;

  const priceStr =
    details.minPrice != null && details.maxPrice != null
      ? `$${details.minPrice}–$${details.maxPrice}`
      : details.minPrice != null
      ? `From $${details.minPrice}`
      : details.maxPrice != null
      ? `Up to $${details.maxPrice}`
      : null;

  return (
    <div className="home-tm-chip">
      <div className="home-tm-banner" style={{ background: banner.gradient }}>
        <div className="home-tm-banner-genre" style={{ color: banner.labelColor }}>
          <BannerIcon size={11} />
          {genreLabel}
        </div>
        {details.statusCode && (
          <span className="home-tm-status">{details.statusCode.replace(/_/g, ' ')}</span>
        )}
      </div>

      <div className="home-tm-body">
        <div className="home-tm-top">
          <div className="home-event-type">
            <Tag size={11} />
            Ticketmaster
          </div>
          {impact && <ImpactBadge level={impact} />}
        </div>

        <div className="home-event-name">{event.name}</div>

        <div className="home-tm-grid">
          {details.venueName && (
            <>
              <div className="home-tm-detail">
                <MapPin size={11} />
                {details.venueName}
              </div>
              <div className="home-tm-detail">
                {details.venueCity}
                {details.venueState ? `, ${details.venueState}` : ''}
              </div>
            </>
          )}
          {priceStr && (
            <div className="home-tm-detail home-tm-detail--price">
              <DollarSign size={11} />
              {priceStr}
            </div>
          )}
          <div className="home-tm-detail">
            <Clock size={11} />
            {startTime}
          </div>
        </div>

        <div className="home-tm-footer">
          {details.ticketUrl ? (
            <a
              className="home-tm-ticket-link"
              href={details.ticketUrl}
              target="_blank"
              rel="noreferrer"
            >
              <ExternalLink size={11} />
              Get tickets
            </a>
          ) : (
            <span />
          )}
          <span className="home-tm-time mono">{timeRange}</span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// EspnSportChip (internal)
// ---------------------------------------------------------------------------

function EspnSportChip({
  event,
  details,
}: {
  event: CantoEvent;
  details: SportsDetails;
}): React.ReactElement {
  const banner = TM_BANNER_META['sporting_event']!;
  const BannerIcon = banner.Icon;
  const sportLabel = details.sport.charAt(0).toUpperCase() + details.sport.slice(1);

  const multiDay = event.startAt.slice(0, 10) !== event.endAt.slice(0, 10);
  const timeRange = multiDay ? 'All day' : `${formatTime(event.startAt)} – ${formatTime(event.endAt)}`;
  const startTime = multiDay ? 'All day' : formatTime(event.startAt);

  const impact: 'high' | 'medium' | 'low' | null =
    event.expectedImpact === 'low' || event.expectedImpact === 'medium' || event.expectedImpact === 'high'
      ? event.expectedImpact
      : null;

  return (
    <div className="home-tm-chip">
      <div className="home-tm-banner" style={{ background: banner.gradient }}>
        <div className="home-tm-banner-genre" style={{ color: banner.labelColor }}>
          <BannerIcon size={11} />
          {sportLabel}
        </div>
      </div>

      <div className="home-tm-body">
        <div className="home-tm-top">
          <div className="home-event-type">
            <Tag size={11} />
            ESPN
          </div>
          {impact && <ImpactBadge level={impact} />}
        </div>

        <div className="home-event-name">{event.name}</div>

        <div className="home-tm-grid">
          {details.venueName && (
            <>
              <div className="home-tm-detail">
                <MapPin size={11} />
                {details.venueName}
              </div>
              <div className="home-tm-detail">
                {details.venueCity}
                {details.venueState ? `, ${details.venueState}` : ''}
              </div>
            </>
          )}
          {details.broadcastNetwork && (
            <div className="home-tm-detail">
              {details.broadcastNetwork}
            </div>
          )}
          <div className="home-tm-detail">
            <Clock size={11} />
            {startTime}
          </div>
        </div>

        <div className="home-tm-footer">
          {details.espnEventUrl ? (
            <a
              className="home-tm-ticket-link"
              href={details.espnEventUrl}
              target="_blank"
              rel="noreferrer"
            >
              <ExternalLink size={11} />
              View on ESPN
            </a>
          ) : (
            <span />
          )}
          <span className="home-tm-time mono">{timeRange}</span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// EventChip (exported)
// ---------------------------------------------------------------------------

export function EventChip({ event }: { event: CantoEvent }): React.ReactElement {
  if (event.source?.startsWith('ticketmaster') && event.ticketmasterDetails) {
    return <TicketmasterEventChip event={event} details={event.ticketmasterDetails} />;
  }
  if (event.source?.startsWith('espn_') && event.sportsDetails) {
    return <EspnSportChip event={event} details={event.sportsDetails} />;
  }

  const iconName = TYPE_ICON[event.type] ?? 'menu';
  const label = EVENT_TYPE_LABEL[event.type] ?? event.type;
  const multiDay = event.startAt.slice(0, 10) !== event.endAt.slice(0, 10);
  const time = multiDay
    ? 'All day'
    : `${formatTime(event.startAt)} – ${formatTime(event.endAt)}`;
  const impact: 'high' | 'medium' | 'low' | null =
    event.expectedImpact === 'low' || event.expectedImpact === 'medium' || event.expectedImpact === 'high'
      ? event.expectedImpact
      : null;

  return (
    <div className="home-event-chip">
      <div className="home-event-chip-top">
        <div className="home-event-type">
          <Icon name={iconName} size={11} />
          {label}
        </div>
        {impact && <ImpactBadge level={impact} />}
      </div>
      <div className="home-event-name">{event.name}</div>
      <div className="home-event-meta">
        <span className="mono">{time}</span>
      </div>
      {event.notes && <div className="home-event-notes">{event.notes}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ReservationChip (exported)
// ---------------------------------------------------------------------------

const STATUS_LABEL: Record<string, string> = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  cancelled: 'Cancelled',
};

export function ReservationChip({ reservation }: { reservation: Reservation }): React.ReactElement {
  return (
    <div className={`home-event-chip reservation-chip reservation-chip--${reservation.status}`}>
      <div className="home-event-chip-top">
        <div className="home-event-type">
          <CalendarClock size={11} />
          Reservation
        </div>
        <span className={`reservation-status-badge reservation-status-badge--${reservation.status}`}>
          {STATUS_LABEL[reservation.status] ?? reservation.status}
        </span>
      </div>
      <div className="home-event-name">{reservation.name}</div>
      <div className="home-event-meta">
        <span className="mono">{formatTime(reservation.reservedAt)}</span>
        <span className="reservation-party">
          <Users size={11} />
          {reservation.partySize}
        </span>
      </div>
      {reservation.specialRequests && (
        <div className="home-event-notes">{reservation.specialRequests}</div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DayColumn (exported)
// ---------------------------------------------------------------------------

export function DayColumn({
  day,
  events,
  reservations,
  reservationCount,
  isToday,
  isLoading,
}: {
  day: Date;
  events: CantoEvent[];
  reservations?: Reservation[];
  reservationCount?: number;
  isToday: boolean;
  isLoading?: boolean;
}): React.ReactElement {
  const resCount = reservations != null ? reservations.length : (reservationCount ?? 0);

  return (
    <div className={`home-day${isToday ? ' home-day--today' : ''}`}>
      <div className="home-day-header">
        <div>
          <div className="uppercase-label">
            {isToday ? 'Today' : day.toLocaleDateString([], { weekday: 'long' })}
          </div>
          <div className="home-day-date">
            {day.toLocaleDateString([], { month: 'short', day: 'numeric' })}
          </div>
        </div>
        {!isLoading && (
          <div className="home-day-count mono">
            {events.length === 0 && resCount === 0
              ? 'Clear'
              : [
                  events.length > 0 ? `${events.length} ${events.length === 1 ? 'event' : 'events'}` : null,
                  resCount > 0 ? `${resCount} ${resCount === 1 ? 'reservation' : 'reservations'}` : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
          </div>
        )}
      </div>
      {isLoading ? (
        <div className="home-day-empty">Loading…</div>
      ) : events.length === 0 && resCount === 0 ? (
        <div className="home-day-empty">Nothing scheduled.</div>
      ) : (
        <>
          {events.map((ev) => <EventChip key={ev.id} event={ev} />)}
          {reservations?.map((r) => <ReservationChip key={r.id} reservation={r} />)}
        </>
      )}
    </div>
  );
}
